import { Flex, FormControl, FormErrorMessage, FormLabel, Text, Textarea, useColorModeValue } from '@chakra-ui/react';
import { memo, useState, useEffect, useRef, forwardRef } from 'react';

const TextareaField = memo(
   forwardRef(
      (
         { name, label, value, onChange, errors = {}, errorName, isDisabled = false, required = false, ...props },
         ref,
      ) => {
         const [localValue, setLocalValue] = useState(value);
         const [isFocused, setIsFocused] = useState(false);
         const timeoutRef = useRef(null);

         const textColor = useColorModeValue('gray.800', 'white');
         const bgColor = useColorModeValue('white', '#2d3748');
         const isFloating = !!(localValue !== '' && localValue !== null && localValue !== undefined);

         // Sync external value changes to local state only when not typing
         useEffect(() => {
            if (!isFocused) {
               setLocalValue(value);
            }
         }, [value, isFocused]);

         const handleChange = (e) => {
            const newValue = e.target.value;
            setLocalValue(newValue); // Update immediately for smooth typing

            // Clear previous timeout
            if (timeoutRef.current) {
               clearTimeout(timeoutRef.current);
            }

            // Debounce the parent onChange
            timeoutRef.current = setTimeout(() => {
               onChange?.({
                  target: {
                     name,
                     value: newValue,
                  },
               });
            }, 300); // Increased delay
         };

         // Cleanup timeout on unmount
         useEffect(() => {
            return () => {
               if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current);
               }
            };
         }, []);

         return (
            <FormControl position="relative" isInvalid={errors[errorName]}>
               <Textarea
                  ref={ref}
                  id={name}
                  value={localValue ?? ''}
                  onChange={handleChange}
                  name={name}
                  isDisabled={isDisabled}
                  onFocus={() => setIsFocused(true)}
                  onBlur={(e) => {
                     setIsFocused(false);
                     // Ensure final value is synced on blur
                     if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                     }
                     onChange?.(e);
                  }}
                  borderRadius="md"
                  borderColor="gray.300"
                  paddingTop={1}
                  paddingBottom="0.25rem"
                  {...props}
               />
               <FormLabel
                  htmlFor={name}
                  position="absolute"
                  top={isFocused || isFloating ? '-8px' : '5px'}
                  left="0.75rem"
                  fontSize={isFocused || isFloating ? 'xs' : 'md'}
                  color={textColor}
                  pointerEvents="none"
                  backgroundColor={bgColor}
                  px="1"
                  zIndex={1}
                  transition="all 0.2s ease-in-out">
                  <Flex>
                     {label}
                     {required && (
                        <Text color="red.500" ml={1}>
                           *
                        </Text>
                     )}
                  </Flex>
               </FormLabel>
               {errors[errorName] && <FormErrorMessage>{errors[errorName]}</FormErrorMessage>}
            </FormControl>
         );
      },
   ),
);

TextareaField.displayName = 'TextareaField';

export default TextareaField;
